import React, { useMemo } from "react";
import { Card, CircularProgress } from "@firecms/ui";

import { useEventStats } from "../hooks/useEvents";
import { useAdminTranslation } from "../i18n/useAdminTranslation";
import type { EventCollectionId } from "../workspace/collectionIds";

import { TeamCard } from "./TeamCard";
import SearchBar from "./SearchBar";
import {
  statsMuted,
  statsPageTitle,
  statsProgressTrack,
  statsSectionTitle,
} from "./statsViewTheme";

type TableListViewProps = {
  eventId?: EventCollectionId;
};

export function TableListView({ eventId }: TableListViewProps) {
  const { t } = useAdminTranslation();
  const [searchTerm, setSearchTerm] = React.useState<string>("");

  const { eventStats, loading } = useEventStats(eventId);

  const mParts = eventStats?.availableSlots || 0;
  const rParts = eventStats?.totalSlots || 0;
  const aParts = eventStats?.occupiedSlots || 0;

  const mTeams = eventStats?.teamSlotCount || 0;
  const rTeams = eventStats?.totalTeams || 0;
  const aTeams = eventStats?.activeTeams || 0;

  const pct = (n: number, d: number) =>
    d > 0 ? Math.max(0, Math.min(100, Math.round((n / d) * 100))) : 0;

  const vmTeams = pct(rTeams, mTeams);
  const vaTeams = pct(aTeams, mTeams);

  const vmParts = pct(rParts, mParts);
  const vaParts = pct(aParts, mParts);

  const filteredEventStats = useMemo(() => {
    if (!eventStats) return;
    if (!searchTerm) return eventStats;

    const filteredTeams = eventStats.teamStats?.filter((teamStats) => {
      const teamNumberLabel = String(
        teamStats.team.values.teamNumber ?? ""
      ).toLowerCase();
      const searchTermLower = searchTerm.toLowerCase();

      if (teamNumberLabel.includes(searchTermLower)) {
        return true;
      }

      const filteredParticipants = teamStats.participants.filter((participant) => {
        const participantLabel = (
          participant.values.participantNumber != null
            ? `participant ${participant.values.participantNumber}`
            : participant.id
        ).toLowerCase();
        return participantLabel.includes(searchTermLower);
      });

      if (filteredParticipants.length > 0) {
        return true;
      }

      return false;
    });

    return {
      ...eventStats,
      teamStats: filteredTeams,
    };
  }, [eventStats, searchTerm]);

  const handleOnSearch = (searchTerm: string) => {
    setSearchTerm(searchTerm);
  };

  return (
    <div className="flex h-full w-full bg-surface-50 dark:bg-surface-900">
      <div className="mx-auto w-full max-w-6xl px-4 py-8">
        <h1 className={statsPageTitle}>{t("eventStats.viewTitle")}</h1>

        {loading && (
          <div className="mt-6 flex w-full justify-center">
            <CircularProgress size="medium" />
          </div>
        )}

        {!loading && (
          <>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Card className="border-surface-200 dark:border-surface-700 dark:bg-surface-800">
                <div className="p-4">
                  <div className={statsSectionTitle}>Participants</div>
                  <div className={`mt-1 text-xs ${statsMuted}`}>
                    Active {aParts} / Registered {rParts} (max {mParts})
                  </div>

                  <div className="mt-4 space-y-3">
                    <div>
                      <div
                        className={`flex items-center justify-between text-xs ${statsMuted}`}
                      >
                        <span>Registered</span>
                        <span>{vmParts}%</span>
                      </div>
                      <div className={`mt-1 ${statsProgressTrack}`}>
                        <div
                          className="h-full rounded-full bg-amber-500 dark:bg-amber-400"
                          style={{ width: `${vmParts}%` }}
                        />
                      </div>
                    </div>
                    <div>
                      <div
                        className={`flex items-center justify-between text-xs ${statsMuted}`}
                      >
                        <span>Active</span>
                        <span>{vaParts}%</span>
                      </div>
                      <div className={`mt-1 ${statsProgressTrack}`}>
                        <div
                          className="h-full rounded-full bg-emerald-500 dark:bg-emerald-400"
                          style={{ width: `${vaParts}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </Card>

              <Card className="border-surface-200 dark:border-surface-700 dark:bg-surface-800">
                <div className="p-4">
                  <div className={statsSectionTitle}>Teams</div>
                  <div className={`mt-1 text-xs ${statsMuted}`}>
                    Active {aTeams} / Registered {rTeams} (max {mTeams})
                  </div>

                  <div className="mt-4 space-y-3">
                    <div>
                      <div
                        className={`flex items-center justify-between text-xs ${statsMuted}`}
                      >
                        <span>Registered</span>
                        <span>{vmTeams}%</span>
                      </div>
                      <div className={`mt-1 ${statsProgressTrack}`}>
                        <div
                          className="h-full rounded-full bg-amber-500 dark:bg-amber-400"
                          style={{ width: `${vmTeams}%` }}
                        />
                      </div>
                    </div>
                    <div>
                      <div
                        className={`flex items-center justify-between text-xs ${statsMuted}`}
                      >
                        <span>Active</span>
                        <span>{vaTeams}%</span>
                      </div>
                      <div className={`mt-1 ${statsProgressTrack}`}>
                        <div
                          className="h-full rounded-full bg-emerald-500 dark:bg-emerald-400"
                          style={{ width: `${vaTeams}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            <div className="mt-6">
              <SearchBar onSearch={handleOnSearch} />
            </div>

            <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filteredEventStats?.teamStats?.map((teamStats, index) => (
                <TeamCard
                  key={teamStats.team.id ?? index}
                  teamStats={teamStats}
                />
              ))}
            </div>

            {filteredEventStats?.teamStats?.length === 0 ? (
              <p className={`mt-8 text-center text-sm ${statsMuted}`}>
                No teams match your search.
              </p>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
